/**
 * 広告画像生成（Ad Image）hooks
 * - useAdImages: 自分が閲覧可能な ad images 一覧
 * - useAdImage: 単一 ad image
 * - useCreateEmptyAdImage: 空ドキュメント新規作成 → エディタ画面へ即遷移
 * - useDuplicateAdImage: 既存 ad image をまるごと複製（一覧の「複製」ボタン）
 * - useUpdateAdImage: メタ・slides の保存（debounce 付き保存はコンポーネント側）
 * - useGenerateAdImage / useOptimizeAdImageText: Callable Function
 */
import type {
  AdImage,
  AdImageConfig,
  AdImageSlide,
  AdElement,
  AdSizePresetId,
  GenerateAdImageResult,
  GenerateAdImageVariantsResult,
  OptimizeAdImageTextResult,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions as fns } from '@/lib/firebase';
import { safeAddDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface AdImageRecord extends AdImage {
  id: string;
}

export function useAdImages() {
  return useQuery({
    queryKey: ['adImages'],
    queryFn: async (): Promise<AdImageRecord[]> => {
      const snap = await getDocs(query(collection(db, 'adImages'), orderBy('updatedAt', 'desc')));
      return snap.docs.map((d) => {
        const { id: _idIgnored, ...rest } = d.data() as AdImage;
        void _idIgnored;
        return { id: d.id, ...rest };
      });
    },
  });
}

export function useAdImage(id: string | null | undefined) {
  return useQuery({
    queryKey: ['adImages', id],
    enabled: !!id,
    queryFn: async (): Promise<AdImageRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'adImages', id));
      if (!snap.exists()) return null;
      const { id: _idIgnored, ...rest } = snap.data() as AdImage;
      void _idIgnored;
      return { id: snap.id, ...rest };
    },
  });
}

const DEFAULT_CONFIG: AdImageConfig = {
  audience: 'btoc',
  productType: 'product',
  mediaKind: 'image',
  platform: 'instagram',
  sizePresetId: 'instagram_feed_1x1',
  adType: '03',
};

export function useCreateEmptyAdImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      ownerId: string;
      title?: string;
      config?: Partial<AdImageConfig>;
    }): Promise<string> => {
      const config: AdImageConfig = { ...DEFAULT_CONFIG, ...(args.config ?? {}) };
      const ref = await safeAddDoc(collection(db, 'adImages'), {
        ownerId: args.ownerId,
        title: args.title ?? '無題の広告画像',
        status: 'draft',
        config,
        slides: [],
        sourceCampaignId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adImages'] }),
  });
}

/**
 * 既存の広告画像を複製する。スライド（要素・背景・配置）と設定をそのままコピーし、
 * タイトルに「（コピー）」を付けて新規作成する。画像ファイルは複製せず同じファイルを参照する
 * （バリアント生成と同じ扱い。広告画像の削除では画像ファイルは消えない）。
 * 作成者は複製した人になる（rules: ownerId == 自分）。
 */
export function useDuplicateAdImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { sourceId: string; ownerId: string }): Promise<string> => {
      // 一覧のキャッシュではなく最新の保存内容から複製する
      const snap = await getDoc(doc(db, 'adImages', args.sourceId));
      if (!snap.exists()) throw new Error('複製元が見つかりません');
      const src = snap.data() as AdImage;
      // 生成中・失敗のまま複製すると表示が固まるため下書きに戻す
      const status = src.status === 'ready' ? 'ready' : 'draft';
      const ref = await safeAddDoc(collection(db, 'adImages'), {
        ownerId: args.ownerId,
        title: `${src.title}（コピー）`,
        status,
        config: src.config,
        slides: src.slides ?? [],
        sourceCampaignId: src.sourceCampaignId ?? null,
        parentAdImageId: src.parentAdImageId ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return ref.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adImages'] }),
  });
}

interface UpdateAdImageArgs {
  id: string;
  data: Partial<AdImage>;
}

export function useUpdateAdImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateAdImageArgs) => {
      await safeUpdateDoc(doc(db, 'adImages', id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['adImages'] });
      qc.invalidateQueries({ queryKey: ['adImages', id] });
    },
  });
}

interface UpdateSlideArgs {
  id: string;
  slides: AdImageSlide[];
}

/** slides 配列を丸ごと書き換える（要素編集・配置変更の保存用） */
export function useUpdateAdImageSlides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, slides }: UpdateSlideArgs) => {
      await safeUpdateDoc(doc(db, 'adImages', id), {
        slides,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['adImages', id] });
    },
  });
}

export function useGenerateAdImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { adImageId: string; slideId?: string }) => {
      const fn = httpsCallable(fns, 'generateAdImage', { timeout: 540_000 });
      const res = await fn(args);
      return res.data as GenerateAdImageResult;
    },
    onSuccess: (_, { adImageId }) => {
      qc.invalidateQueries({ queryKey: ['adImages'] });
      qc.invalidateQueries({ queryKey: ['adImages', adImageId] });
    },
    onError: (_err, { adImageId }) => {
      // 失敗時も Firestore 側で status='failed' / errorMessage がセットされているため再取得
      qc.invalidateQueries({ queryKey: ['adImages', adImageId] });
    },
  });
}

export function useDeleteAdImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'adImages', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adImages'] }),
  });
}

export function useGenerateAdImageVariants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      sourceAdImageId: string;
      targetSizePresetIds: AdSizePresetId[];
    }) => {
      const fn = httpsCallable(fns, 'generateAdImageVariants', { timeout: 540_000 });
      const res = await fn(args);
      return res.data as GenerateAdImageVariantsResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adImages'] });
    },
  });
}

export function useOptimizeAdImageText() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { adImageId: string; slideId: string; elementId?: string }) => {
      const fn = httpsCallable(fns, 'optimizeAdImageText', { timeout: 120_000 });
      const res = await fn(args);
      return res.data as OptimizeAdImageTextResult;
    },
    onSuccess: (_, { adImageId }) => {
      qc.invalidateQueries({ queryKey: ['adImages', adImageId] });
    },
  });
}

/** Phase 33.3 / 34.1: 背景画像の AI 最適化（4 モード）。Firestore は更新せず新旧 URL を返す。 */
export function useOptimizeAdImageBackground() {
  return useMutation({
    mutationFn: async (args: {
      adImageId: string;
      slideId: string;
      mode: 'prompt_only' | 'with_reference' | 'upscale_only' | 'with_reference_custom';
      customPrompt?: string;
    }) => {
      const fn = httpsCallable(fns, 'optimizeAdImageBackground', { timeout: 240_000 });
      const res = await fn(args);
      return res.data as {
        slideId: string;
        newUrl: string;
        newStoragePath: string;
        currentUrl: string;
      };
    },
  });
}

/** Phase 34.3: 旧 Storage パス（per-adImage subfolder）→ フラット構造へ移行 */
export function useMigrateAdImageStorage() {
  return useMutation({
    mutationFn: async (args: { dryRun?: boolean; deleteOld?: boolean }) => {
      const fn = httpsCallable(fns, 'migrateAdImageStorage', { timeout: 540_000 });
      const res = await fn(args);
      return res.data as {
        scannedDocs: number;
        scannedUrls: number;
        migrated: number;
        alreadyMigrated: number;
        skipped: number;
        deleted: number;
        errors: { adImageId: string; reason: string }[];
      };
    },
  });
}

/** クライアント側のヘルパー：slides 配列の特定 slide / element を更新 */
export function patchSlideElement(
  slides: AdImageSlide[],
  slideId: string,
  elementId: string,
  patch: Partial<AdElement>,
): AdImageSlide[] {
  return slides.map((s) =>
    s.id !== slideId
      ? s
      : {
          ...s,
          elements: s.elements.map((el) =>
            el.id !== elementId ? el : ({ ...el, ...patch } as AdElement),
          ),
        },
  );
}

export function patchSlide(
  slides: AdImageSlide[],
  slideId: string,
  patch: Partial<AdImageSlide>,
): AdImageSlide[] {
  return slides.map((s) => (s.id !== slideId ? s : { ...s, ...patch }));
}
